import type { ReactNode } from "react";
import { FormItem } from "@freightsimple/ui-core/FormLayout/FormItem";
import { Grid } from "@freightsimple/ui-core/PageLayout/Grid";
import { ShowFormErrors } from "@freightsimple/ui-core/FormLayout/ShowFormErrors";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { T } from "@freightsimple/ui-core/Typography/T";
import { getAccessorials } from "@/Data/getAccessorials";
import LocationTypeDropdown from "@/Shared/Locations/Dropdowns/LocationTypeDropdown";
import LocationTypeQuestionBubble from "@/Shared/Locations/Bubbles/LocationTypeQuestionBubble";
import LocationsAccessorialsInput from "../Inputs/LocationsAccessorialsInput";
import type { PostalCodeChangeEvent } from "../Inputs/LocationsPostalCodeInput";
import LocationsPostalCodeInput from "../Inputs/LocationsPostalCodeInput";
import type {
  AddLocationAddressType,
  AddLocationLocationType,
} from "@/Shared/Types/locationTypes";
import type { LocationErrors } from "../Validators/locationsErrorMessagesForLocation";
import { startsWithNumber } from "@/Helpers/formatPostalCode";
import type { Accessorials } from "@/Shared/types";
import type { LatitudeLongitude } from "@freightsimple/generated-dashboard-openapi-client/models/LatitudeLongitude";
import type { Location } from "@freightsimple/generated-dashboard-openapi-client/models/Location";
import { LocationContext } from "@freightsimple/generated-dashboard-openapi-client/models/LocationContext";
import { LocationType } from "@freightsimple/generated-dashboard-openapi-client/models/LocationType";
import { DistributionWarehouseBrandDropdown } from "@/Shared/Locations/Dropdowns/DistributionWarehouseBrandDropdown";
import DistributionWarehouseBrandQuestionBubble from "@/Shared/Locations/Bubbles/DistributionWarehouseBrandQuestionBubble";

interface LocationDetailsProps {
  locationErrorMessages: LocationErrors;
  locationWarningMessages: LocationErrors;
  forceValidation: boolean;
  address: AddLocationAddressType;
  location: AddLocationLocationType | Location;
  onPostalCodeChange: (
    _: PostalCodeChangeEvent,
    distributionWarehouseName: string | undefined,
  ) => void;
  showMap: boolean;
  locationContext: LocationContext;
  latitudeLongitude: LatitudeLongitude | undefined;
  setLocationType: (_: LocationType) => void;
  setDistributionWarehouseBrand: (_: string | undefined) => void;
  accessorials: Accessorials;
  setSchedulingAccessorials: (_: Accessorials) => void;
  setLogisticsAccessorials: (_: Accessorials) => void;
  disablePostalCodeEntry: boolean;
  distributionWarehouseName: string | undefined;
}

function LocationsDetails(props: LocationDetailsProps) {
  const {
    locationErrorMessages,
    locationWarningMessages,
    forceValidation,
    address,
    onPostalCodeChange,
    showMap,
    location,
    locationContext,
    setLocationType,
    setDistributionWarehouseBrand,
    accessorials,
    setSchedulingAccessorials,
    setLogisticsAccessorials,
  } = props;

  function displayNameForLocationContext(
    locationContext: LocationContext | undefined,
  ) {
    if (locationContext === LocationContext.Pickup) {
      return "Pickup";
    }

    if (locationContext === LocationContext.Delivery) {
      return "Delivery";
    }

    return undefined;
  }

  const displayName = displayNameForLocationContext(props.locationContext);
  const displayNameWithSpace = (displayName || "") + " ";

  function labelForPostalCode() {
    const postalCode = address.postalCode;

    if (postalCode === undefined || postalCode === "") {
      return `${displayNameWithSpace}Postal Code / Zip Code`;
    }

    if (!startsWithNumber(postalCode)) {
      return `${displayNameWithSpace}Postal Code`;
    } else {
      return `${displayNameWithSpace}Zip Code`;
    }
  }

  const postalCodeLabel = labelForPostalCode();

  const schedulingAccessorials = getAccessorials(
    locationContext,
    location.locationType,
    "SCHEDULING_",
  );

  const selectedSchedulingAccessorials =
    accessorials.filter((a) => a.startsWith("SCHEDULING_")) || [];

  // Cargo's standalone <Label> — a bold 16px heading, sitting above the field
  // as its own sibling. Not FormItem's `label`, which is the small muted one a
  // dense field grid uses (the line-item fields are those) and whose wrapper
  // would tint this grey.
  function PanelLabel_({ children }: { children: ReactNode }) {
    return (
      <T size="base" weight="bold">
        {children}
      </T>
    );
  }

  return (
    // Two equal columns filling the panel, with a wide gutter — the shape the
    // quoting form has always had. The fields fill their column rather than
    // sitting at a fixed width, so the panel has no dead space on the right.
    // 340 is what the column actually needs: "Delivery Postal Code / Zip Code"
    // is 332px and is the widest thing in it — wider than the 260px placeholder
    // below it. Any narrower and the label wraps instead of the grid dropping to
    // one column. The dialogs used to override this down to 280 so they could
    // stay two-up in a narrower frame; they are wide enough now, and 280 only
    // bought squeezing instead of an honest drop to one column.
    <Grid columns={2} gap="xl" minColumnWidth={340}>
      <Stack gap={6}>
        <Stack gap={2}>
          <PanelLabel_>{postalCodeLabel}</PanelLabel_>
          <LocationsPostalCodeInput
            errorMessage={locationErrorMessages.address.postalCode}
            warningMessage={locationWarningMessages.address.postalCode}
            forceValidation={forceValidation}
            postalCode={address?.postalCode || ""}
            city={address?.city || ""}
            stateOrProvinceCode={address?.stateOrProvinceCode || ""}
            countryCode={address.countryCode}
            onChange={onPostalCodeChange}
            width="full"
            displayName={displayName}
            enabled={!props.disablePostalCodeEntry}
            showMap={showMap}
          />
        </Stack>

        {/* The location-type error (e.g. purely-residential moves aren't
            supported) blocks booking, so it must be shown here — otherwise the
            user only sees the generic "correct the above errors" banner with
            no indication of which field. */}
        <Stack gap={2}>
          <PanelLabel_>
            {displayName
              ? `Choose ${displayName} Location Type`
              : "Choose Location Type"}
            <LocationTypeQuestionBubble displayName={displayName} />
          </PanelLabel_>
          <ShowFormErrors show={forceValidation}>
            <FormItem
              error={locationErrorMessages.locationType}
              warning={locationWarningMessages.locationType}
              fillWidth
            >
              <LocationTypeDropdown
                locationType={location.locationType}
                setLocationType={setLocationType}
              />
            </FormItem>
          </ShowFormErrors>
        </Stack>

        {location.locationType === LocationType.DistributionWarehouse && (
          <Stack gap={2}>
            <PanelLabel_>
              Distribution Warehouse Brand
              <DistributionWarehouseBrandQuestionBubble />
            </PanelLabel_>
            <DistributionWarehouseBrandDropdown
              selectedItem={location.distributionWarehouseBrand}
              onOptionSelected={setDistributionWarehouseBrand}
            />
          </Stack>
        )}
      </Stack>

      <Stack gap={6}>
        {schedulingAccessorials.length > 0 && (
          <Stack gap={2}>
            <T size="base" weight="bold">
              Scheduling
            </T>
            <LocationsAccessorialsInput
              accessorials={schedulingAccessorials}
              selectedAccessorials={selectedSchedulingAccessorials}
              selectedAccessorialsChanged={setSchedulingAccessorials}
              locationContext={props.locationContext}
              locationType={
                props.location.locationType ?? LocationType.Warehouse
              }
            />
          </Stack>
        )}
        <Stack gap={2}>
          <T size="base" weight="bold">
            Logistics
          </T>
          <LocationsAccessorialsInput
            accessorials={getAccessorials(
              locationContext,
              location.locationType,
              "LOGISTICS_",
            )}
            selectedAccessorials={
              accessorials.filter((a) => a.startsWith("LOGISTICS_")) || []
            }
            selectedAccessorialsChanged={setLogisticsAccessorials}
            locationContext={props.locationContext}
            locationType={props.location.locationType ?? LocationType.Warehouse}
          />
        </Stack>
      </Stack>
    </Grid>
  );
}

export default LocationsDetails;
